import { useRef, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Snackbar, TextField, Typography,
} from '@mui/material'
import { FileText, Plus, Upload } from 'lucide-react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from '../lib/router'
import { AppShell, Crumb, HomeCrumb } from '../components/AppShell'
import { useImportRegionYields, useRegion, useRegionProducers } from '../api/hooks'
import { Eyebrow, Mono } from '../components/primitives'
import { SkeletonScreen } from '../components/Skeleton'
import { Flag } from '../components/Flag'
import { Segmented } from '../components/Segmented'
import { formatDate, num } from '../lib/format'
import { tokens } from '../theme'
import { FieldsTable } from './region/FieldsTable'
import { RegionPolygons, ViewSwitch, type RegionView } from './region/RegionPolygons'
import { ProducerPanel } from './region/ProducerPanel'
import { RegionHistory } from './region/RegionHistory'
import { ExportPlots } from './region/ExportPlots'

const DEFAULT_THRESHOLD = 25
const DEFAULT_SHARE = 0.01

/**
 * A region, field by field, against the FAO national average yield.
 *
 * Geometry compliance says the polygon is where it claims to be. This says the
 * harvest it claims is physically possible on that much land — a field far
 * above the national average is either a data-entry error or production from
 * somewhere else being routed through a compliant polygon.
 */
export function RegionDetail() {
  const navigate = useNavigate()
  const { regionId } = useParams<{ regionId: string }>()
  const [params, setParams] = useSearchParams()
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  // The controls live in the URL, so a filtered view is a link someone can send.
  const threshold = Number(params.get('threshold') ?? DEFAULT_THRESHOLD) || DEFAULT_THRESHOLD
  const shareThreshold = Number(params.get('share') ?? DEFAULT_SHARE) || DEFAULT_SHARE
  const window = params.get('window') === '10' ? 10 : 5
  const fo = params.get('fo')
  const farmerId = params.get('farmer')
  const suspicious = params.get('suspicious') === 'true'
  // Which harvest season the yield view reads; none means the latest declared.
  const season = params.get('season') ?? undefined
  /**
   * Which question this screen is answering: the polygons, or the yield.
   *
   * The map by default. Opening a region to be asked for a file is backwards
   * — the polygons and what the checks made of them are the reason anyone
   * opens one, and the yield comparison is a second question about the same
   * set.
   */
  const view = (params.get('view') ?? 'map') as RegionView
  // Which checks the map and table are narrowed to: in the address, so the
  // report's "view" links land here filtered.
  const checkFilter = (params.get('check') ?? '').split(',').filter(Boolean)

  // Composed against a ref rather than through setParams' updater form: React
  // batches two control changes in one tick, and the updater is handed the
  // params from the location, which has not committed yet — so the second
  // change would silently drop the first.
  const paramsRef = useRef(params)
  paramsRef.current = params
  const update = (edit: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(paramsRef.current)
    edit(next)
    paramsRef.current = next
    setParams(next)
  }
  const setParam = (key: string, value: string | null) =>
    update((p) => {
      if (value === null || value === '') p.delete(key)
      else p.set(key, value)
    })

  const { data: detail, isLoading } = useRegion(regionId)
  const importYields = useImportRegionYields(regionId)

  const group = fo ? 'farmer' : 'fo'
  const { data: producerData, isPlaceholderData: producersStale } = useRegionProducers(regionId, {
    season,
    threshold: String(threshold),
    window: String(window),
    share_threshold: String(shareThreshold),
    group,
    fo: fo ?? undefined,
  })

  const region = detail?.region
  const crumbs = [
    <HomeCrumb key="a" />,
    <Crumb key="b" to="/regions">Regions</Crumb>,
    <Crumb key="c" mono>{region?.name ?? regionId?.slice(0, 8)}</Crumb>,
  ]

  // dragenter and dragleave fire for every child the pointer crosses, so the
  // overlay is driven by a depth count rather than by the last event seen.
  const [dragging, setDragging] = useState(false)
  const dragDepth = useRef(0)

  const onFile = (file: File | undefined) => {
    if (!file) return
    if (!/\.csv$/i.test(file.name)) {
      setError(`${file.name} is not a CSV. Yield data is a CSV of plot_ref, crop, season and production_kg.`)
      return
    }
    importYields.mutate(file, {
      onSuccess: (r) =>
        setNotice(
          `Imported ${num(r.rows)} declarations · ${num(r.matched)} matched` +
          (r.unmatched.length ? ` · ${num(r.unmatched.length)} unmatched` : ''),
        ),
      onError: (e) => setError((e as Error).message),
    })
  }

  const dropHandlers = {
    onDragEnter: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes('Files')) return
      dragDepth.current += 1
      setDragging(true)
    },
    onDragOver: (e: React.DragEvent) => e.preventDefault(),
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) setDragging(false)
    },
    onDrop: (e: React.DragEvent) => {
      e.preventDefault()
      dragDepth.current = 0
      setDragging(false)
      onFile(e.dataTransfer.files?.[0])
    },
  }

  const importButton = (
    <>
      <input
        ref={fileInput}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => {
          onFile(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      <Button
        size="small"
        variant="outlined"
        startIcon={<Upload size={14} strokeWidth={1.5} />}
        disabled={importYields.isPending}
        onClick={() => fileInput.current?.click()}
      >
        {importYields.isPending ? 'Importing…' : 'Import yield data'}
      </Button>
    </>
  )

  if (isLoading || !detail || !region) {
    return (
      <AppShell crumbs={crumbs} rail={false}>
        <SkeletonScreen wide />
      </AppShell>
    )
  }

  // Polygons enter a region the same way they enter the product: uploaded
  // or drawn straight into it, then checked. The region goes along in the
  // URL so the picker opens on this one rather than making you find it.
  const addPolygons = (
    <Button
      size="small"
      variant="outlined"
      startIcon={<Plus size={14} strokeWidth={1.5} />}
      onClick={() => navigate(`/checks?region=${regionId}`)}
    >
      Add polygons
    </Button>
  )

  // Present in every view, because the switch is how you reach the others —
  // putting it inside one of them left the yield view with no way out.
  const viewSwitch = (
    <ViewSwitch view={view} onView={(v) => setParam('view', v === 'map' ? null : v)} />
  )

  // The switch sits last, hard against the right edge. The header is
  // right-aligned, so anything that appears beside it pushes it along —
  // and the threshold controls only exist on the yield view, which made the
  // switch jump sideways every time you used it.
  const latestRunId = detail.latest_run?.id
  const exportAll = latestRunId && detail.latest_run?.status === 'completed' && (
    <ExportPlots runId={latestRunId} onError={setError} />
  )

  const seasons = detail.yield?.seasons ?? []
  const shownSeason = season ?? String(detail.yield?.season ?? '')
  const seasonSwitch = seasons.length > 1 && (
    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Segmented
        ariaLabel="Season"
        value={shownSeason}
        // The latest is the default, so it stays out of the address.
        onChange={(v) => setParam('season', v === String(detail.yield?.season) ? null : v)}
        options={seasons.map((s) => ({ value: String(s.season), label: String(s.season) }))}
      />
      {/* Beside the switch that was just pressed: this season is loading. */}
      <CircularProgress
        size={14}
        aria-label="Loading season"
        sx={{ color: 'text.disabled', visibility: producersStale ? 'visible' : 'hidden' }}
      />
    </Box>
  )

  const actions = (
    <>
      {seasonSwitch}
      <ThresholdControls
        threshold={threshold}
        window={window}
        onThreshold={(v) => setParam('threshold', String(v))}
        onWindow={(v) => setParam('window', String(v))}
      />
      {importButton}
      {viewSwitch}
    </>
  )

  // Polygons and their verdicts, or the yield benchmark over the same set.
  // Two questions about one region, so one screen with a switch rather than
  // two screens that each hide half of it.
  //
  // Ahead of the no-yield branch below on purpose: whether the checks ran
  // has nothing to do with whether a harvest file was imported, and putting
  // this second made the map unreachable for any region without one.
  if (view === 'history') {
    return (
      <AppShell crumbs={crumbs} actions={<>{addPolygons}{viewSwitch}</>} rail={false}>
        <RegionHistory
          regionId={regionId!}
          onOpenLatest={() => setParam('view', null)}
          onNotice={setNotice}
        />
        <Notices
          notice={notice} error={error}
          onCloseNotice={() => setNotice(null)} onCloseError={() => setError(null)}
        />
      </AppShell>
    )
  }

  if (view !== 'yield') {
    // The yield threshold and import belong to the yield view; here the
    // polygons and their checks.
    return (
      <AppShell crumbs={crumbs} actions={<>{addPolygons}{exportAll}{viewSwitch}</>} rail={false}>
        <RegionPolygons
          detail={detail}
          view={view}
          checkFilter={checkFilter}
          onCheckFilter={(keys) => setParam('check', keys.join(','))}
        />
        <Notices
          notice={notice} error={error}
          onCloseNotice={() => setNotice(null)} onCloseError={() => setError(null)}
        />
      </AppShell>
    )
  }

  // Nothing to compare against until a yield file has been imported. Falling
  // back to the polygon file's own Yield column would look like data but is
  // unrelated to area, so it would read as noise.
  if (!detail.yield) {
    return (
      <AppShell crumbs={crumbs} actions={<>{addPolygons}{importButton}{viewSwitch}</>} rail={false}>
        <Box sx={{ flex: 1, overflowY: 'auto', px: 8, py: 7 }} {...dropHandlers}>
          <Header region={region} />
          <Dropzone
            dragging={dragging}
            uploading={importYields.isPending}
            onChoose={() => fileInput.current?.click()}
          />
        </Box>
        <Notices
          notice={notice} error={error}
          onCloseNotice={() => setNotice(null)} onCloseError={() => setError(null)}
        />
      </AppShell>
    )
  }

  return (
    <AppShell crumbs={crumbs} actions={actions} rail={false}>
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0, position: 'relative' }} {...dropHandlers}>
        {dragging && <DropOverlay uploading={importYields.isPending} />}
        <ProducerPanel
          season={season ? Number(season) : producerData?.season ?? detail.yield?.season ?? null}
          loading={producersStale}
          benchmark={producerData?.benchmark ?? detail.benchmark}
          totals={producerData?.totals}
          producers={producerData?.producers ?? []}
          group={group}
          selectedFo={fo}
          selected={group === 'fo' ? fo : farmerId}
          shareThreshold={shareThreshold}
          onSelect={(producer) =>
            update((p) => {
              if (group === 'fo') {
                if (producer) p.set('fo', producer)
                else p.delete('fo')
                p.delete('farmer')
              } else if (producer) p.set('farmer', producer)
              else p.delete('farmer')
            })
          }
          onBack={() =>
            update((p) => {
              p.delete('fo')
              p.delete('farmer')
            })
          }
        />
        <FieldsTable
          regionId={regionId!}
          season={season}
          benchmark={producerData?.benchmark ?? detail.benchmark}
          threshold={threshold}
          window={window}
          fo={fo}
          farmerId={farmerId}
          suspicious={suspicious}
          onToggleSuspicious={() => setParam('suspicious', suspicious ? null : 'true')}
        />
      </Box>
      <Notices
        notice={notice} error={error}
        onCloseNotice={() => setNotice(null)} onCloseError={() => setError(null)}
      />
    </AppShell>
  )
}

/**
 * The empty state, which is also the drop target.
 *
 * Matches the dataset dropzone on the upload screen: a file arriving by drag is
 * the same gesture whether it carries polygons or the harvest they declared.
 */
function Dropzone({
  dragging, uploading, onChoose,
}: { dragging: boolean; uploading: boolean; onChoose: () => void }) {
  return (
    <Box
      onClick={onChoose}
      role="button"
      tabIndex={0}
      data-testid="yield-dropzone"
      aria-label="Import yield data"
      onKeyDown={(e) => e.key === 'Enter' && onChoose()}
      sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        maxWidth: 560, mx: 'auto', mt: 10, p: 10, cursor: 'pointer',
        border: `1px dashed ${dragging ? tokens.color.brandTeal : tokens.color.ink300}`,
        borderRadius: `${tokens.radius.md}px`,
        backgroundColor: dragging ? tokens.color.brandTeal100 : tokens.color.ink50,
        color: 'text.secondary', textAlign: 'center',
        transition: `border-color ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}, background ${tokens.motion.dur.fast}ms ${tokens.motion.easeStd}`,
        '&:hover': { borderColor: tokens.color.brandTeal },
      }}
    >
      <Box sx={{ color: dragging ? tokens.color.brandTeal : 'inherit', display: 'flex' }}>
        {dragging ? <FileText size={20} strokeWidth={1.5} /> : <Upload size={20} strokeWidth={1.5} />}
      </Box>
      <Typography variant="h4" sx={{ color: 'text.primary' }}>
        {uploading ? 'Reading the file…' : dragging ? 'Drop to import' : 'No yield declared'}
      </Typography>
      <Typography variant="body2">
        Drop a yield file, or choose one, to compare each field’s declared harvest
        against the FAO national average for its crop.
      </Typography>
      <Mono sx={{ fontSize: 12, color: 'text.disabled', mt: 1 }}>
        CSV · plot_ref, crop, season, production_kg
      </Mono>
    </Box>
  )
}

/** Shown over the loaded screen while a file is being dragged across it. */
function DropOverlay({ uploading }: { uploading: boolean }) {
  return (
    <Box
      sx={{
        position: 'absolute', inset: 0, zIndex: 5, m: 4,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2,
        border: `1px dashed ${tokens.color.brandTeal}`, borderRadius: `${tokens.radius.md}px`,
        backgroundColor: 'rgba(232,242,246,0.94)', color: tokens.color.brandTeal,
        pointerEvents: 'none',
      }}
    >
      <FileText size={22} strokeWidth={1.5} />
      <Typography variant="h4" sx={{ color: tokens.color.brandDeep }}>
        {uploading ? 'Reading the file…' : 'Drop to add declared yields'}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        A plot and season already declared is updated in place; other seasons are kept.
      </Typography>
    </Box>
  )
}

function Header({ region }: {
  region: {
    name: string
    description: string | null
    supplier_name: string | null
    country_code: string | null
    country_name?: string | null
    plot_count: number
    uploads: number
    updated_at: string
  }
}) {
  return (
    <Box sx={{ mb: 6 }}>
      <Eyebrow sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {region.country_code && <Flag code={region.country_code} size={12} />}
        {[region.supplier_name, region.country_name ?? region.country_code,
          `updated ${formatDate(region.updated_at)}`]
          .filter(Boolean)
          .join(' · ')}
      </Eyebrow>
      <Typography variant="h1" sx={{ mb: 2 }}>{region.name}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {region.description ||
          `${num(region.plot_count)} polygons from ` +
          `${num(region.uploads)} ${region.uploads === 1 ? 'upload' : 'uploads'}.`}
      </Typography>
    </Box>
  )
}

/**
 * The threshold, and which FAO baseline it is measured against.
 *
 * The two baselines are about one percent apart, so the toggle only ever moves
 * a handful of fields — which is the honest answer, and why the count is worth
 * showing rather than a bare pass or fail.
 */
function ThresholdControls({
  threshold, window, onThreshold, onWindow,
}: {
  threshold: number
  window: 5 | 10
  onThreshold: (v: number) => void
  onWindow: (v: 5 | 10) => void
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 2 }}>
      <TextField
        type="number"
        size="small"
        value={threshold}
        onChange={(e) => onThreshold(Number(e.target.value))}
        inputProps={{
          step: 5, min: 0, max: 1000,
          'aria-label': 'Percent above the FAO average',
          style: { textAlign: 'left', fontVariantNumeric: 'tabular-nums' },
        }}
        InputProps={{
          endAdornment: (
            <Typography variant="caption" sx={{ color: 'text.disabled', pl: 1, whiteSpace: 'nowrap' }}>
              % over
            </Typography>
          ),
          sx: { height: 32, width: 124, '& input': { height: 30, fontSize: 13 } },
        }}
      />
      <Box sx={{ display: 'flex', p: 0.5, borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100 }}>
        {([5, 10] as const).map((v) => (
          <Box
            key={v}
            component="button"
            type="button"
            aria-pressed={window === v}
            onClick={() => onWindow(v)}
            sx={{
              display: 'flex', alignItems: 'center', px: 2.5, py: 1, border: 0, cursor: 'pointer',
              borderRadius: `${tokens.radius.sm}px`, font: `500 13px ${tokens.font.sans}`,
              whiteSpace: 'nowrap',
              backgroundColor: window === v ? 'background.paper' : 'transparent',
              boxShadow: window === v ? tokens.shadow[1] : 'none',
              color: window === v ? 'text.primary' : 'text.secondary',
            }}
          >
            {v}-year
          </Box>
        ))}
      </Box>
    </Box>
  )
}

function Notices({
  notice, error, onCloseNotice, onCloseError,
}: { notice: string | null; error: string | null; onCloseNotice: () => void; onCloseError: () => void }) {
  return (
    <>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={6000}
        onClose={onCloseNotice}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
          {notice}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={onCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
          {error}
        </Alert>
      </Snackbar>
    </>
  )
}
